import axios, { AxiosResponse } from 'axios';
import { apiClient } from '../../endpoints';
import { Site } from '../../planning';
import { User } from '../../users';
import { FormAnswer } from './formAnswers';
import { FormResponseDocument } from './formResponseDocuments';
import { FormResponseUserWithDetails } from './formResponseUsers';
import { FormTemplate } from './formTemplates';

// ---------------- TYPES ----------------
export interface FormResponse {
    _id: string;
    templateId: string;
    siteId?: Site;
    filledBy: User;
    observations?: string;
    status: number;
    name?: string;
    submittedAt?: string;
    createdAt: string;
    updatedAt?: string;
}

export interface FormResponseWithDetails extends FormResponse {
    answers: FormAnswer[];
    attendees: FormResponseUserWithDetails[];
    documents: FormResponseDocument[];
}

export interface FormResponseWithCount extends FormResponse {
    attendedUsersCount: number;
    documentsCount: number;
    validatedUsersCount: number;
}

export interface FormResponseWithSiteAndFilledBy extends Omit<FormResponse, 'siteId' | 'filledBy'> {
    siteId?: Site;
    filledBy: User;
}

export interface FormResponseWithTemplate extends Omit<FormResponseWithSiteAndFilledBy, 'templateId'> {
    templateId: FormTemplate;
}

export interface FormResponseResponse {
    message: string;
    data: FormResponse;
}

export interface FormResponseWithDetailsResponse {
    message: string;
    data: FormResponseWithDetails;
}

export interface FormResponsesWithCountResponse {
    message: string;
    data: FormResponseWithCount[];
}

export interface FormResponsesWithSiteAndFilledByResponse {
    message: string;
    data: FormResponseWithSiteAndFilledBy[];
}

export interface FormResponsesWithTemplateResponse {
    message: string;
    data: FormResponseWithTemplate[];
}

// ---------------- CREATE / UPDATE ----------------
export interface CreateFormResponseBody {
    templateId: string;
    siteId?: string;
    name?: string;
}

export interface UpdateResponseBody {
    name?: string;
    siteId?: string;
}

// ---------------- CALLS API ----------------

// --- CREATE ---
export const createFormResponse = async (data: CreateFormResponseBody): Promise<FormResponseResponse> => {
    try {
        const response: AxiosResponse<FormResponseResponse> = await apiClient.post('/form-responses', data);
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// --- DELETE (draft only) ---
export const deleteFormResponse = async (id: string): Promise<{ message: string }> => {
    try {
        const response: AxiosResponse<{ message: string }> = await apiClient.delete(`/form-responses/${id}`);
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// --- SEND / SUBMIT (draft only) ---
export const sendFormResponse = async (id: string): Promise<FormResponseResponse> => {
    try {
        const response: AxiosResponse<FormResponseResponse> = await apiClient.post(`/form-responses/${id}/send`);
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// --- GET by Template & User ---
export const getResponsesByTemplateAndUserId = async (templateId: string, userId: string): Promise<FormResponsesWithCountResponse> => {
    try {
        const response: AxiosResponse<FormResponsesWithCountResponse> = await apiClient.get(`/form-responses/template/${templateId}/user/${userId}`);
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// --- GET by Id with details ---
export const getResponseById = async (id: string): Promise<FormResponseWithDetailsResponse> => {
    try {
        const response: AxiosResponse<FormResponseWithDetailsResponse> = await apiClient.get(`/form-responses/${id}`);
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export const getResponsesByTemplateId = async (templateId: string): Promise<FormResponsesWithSiteAndFilledByResponse> => {
    try {
        const response: AxiosResponse<FormResponsesWithSiteAndFilledByResponse> = await apiClient.get(`/form-responses/template/${templateId}`);
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export const getPendingSignaturesByUser = async (userId: string): Promise<FormResponsesWithTemplateResponse> => {
    try {
        const response: AxiosResponse<FormResponsesWithTemplateResponse> = await apiClient.get(`/form-responses/pending/${userId}`);
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// --- UPDATE name ---
export const updateResponse = async (id: string, data: UpdateResponseBody): Promise<FormResponseResponse> => {
    try {
        const response: AxiosResponse<FormResponseResponse> = await apiClient.patch(`/form-responses/${id}`, data);
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};
